import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { softDelete } from '../utils/softDelete';
import ExpedienteDetalle from './ExpedienteDetalle';
import TramiteDetalle from './TramiteDetalle';
import TareaDetalle from '../components/TareaDetalle';
import TareaModal from '../components/TareaModal';
import { useEsAdmin } from '../UserContext';
import { onEntidadChanged } from '../utils/entidadBus';
import { onDataChanged } from '../utils/dataBus';

const FUEROS = ['Civil', 'Penal', 'Laboral', 'Familia', 'Contencioso Administrativo', 'Comercial', 'Sucesorio'];

export default function ClienteDetalle({ clienteId, onClose, onEdit, onUpdate }) {
  const esAdmin = useEsAdmin();
  const [verExpediente, setVerExpediente] = useState(null);
  const [verTramite, setVerTramite] = useState(null);
  const [tareaDetalleId, setTareaDetalleId] = useState(null);
  const [tareaEdit, setTareaEdit] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [expedientes, setExpedientes] = useState([]);
  const [tramites, setTramites] = useState([]);
  const [ingresos, setIngresos] = useState([]);
  const [deudas, setDeudas] = useState([]);
  const [tareasPendientes, setTareasPendientes] = useState([]);
  const [agendaCliente, setAgendaCliente] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabActiva, setTabActiva] = useState('info');
  const [notas, setNotas] = useState([]);
  const [nuevaNota, setNuevaNota] = useState('');
  const [toast, setToast] = useState(null);
  const [modalNuevoExp, setModalNuevoExp] = useState(false);
  const [guardandoExp, setGuardandoExp] = useState(false);
  const [formExp, setFormExp] = useState({ caratula: '', numero: '', fuero: '', posicion_procesal: 'actor', estado_procesal: '', juzgado: '' });
  const [modalNuevoTram, setModalNuevoTram] = useState(false);
  const [guardandoTram, setGuardandoTram] = useState(false);
  const [formTram, setFormTram] = useState({ nombre: '', tipo: '', fecha_inicio: '', fecha_vencimiento: '', estado: 'pendiente', parte_contraria: '' });
  const [gastos, setGastos] = useState([]);
  const [modalNuevoIngreso, setModalNuevoIngreso] = useState(false);
  const [guardandoIngreso, setGuardandoIngreso] = useState(false);
  const [formIngreso, setFormIngreso] = useState({ monto: '', fecha: '', concepto: '', medio_cobro: 'Efectivo' });
  const [modalNuevoGasto, setModalNuevoGasto] = useState(false);
  const [guardandoGasto, setGuardandoGasto] = useState(false);
  const [formGasto, setFormGasto] = useState({ monto: '', fecha: '', concepto: '', categoria: '', medio_pago: 'Efectivo' });
  const [modalNuevaDeuda, setModalNuevaDeuda] = useState(false);
  const [guardandoDeuda, setGuardandoDeuda] = useState(false);
  const [formDeuda, setFormDeuda] = useState({ concepto: '', monto_total: '', fecha_vencimiento: '' });

  useEffect(() => { if (clienteId) fetchTodo(); }, [clienteId]);

  // Mantenerse sincronizado si un expediente/trámite vinculado cambia desde
  // otro lugar (ej: se elimina un trámite mientras se está viendo esta ficha)
  useEffect(() => onEntidadChanged(() => { if (clienteId) fetchTodo(); }), [clienteId]);

  // Automático: cualquier escritura sobre estas tablas refresca la ficha
  useEffect(() => onDataChanged(() => { if (clienteId) fetchTodo(); }, ['expedientes', 'tramites', 'clientes', 'tareas', 'agenda']), [clienteId]);

  async function fetchTodo() {
    setLoading(true);
    const [{ data: cli }, { data: exps }, { data: trams }, { data: ings }, { data: deuds }, { data: gasts }] = await Promise.all([
      supabase.from('clientes').select('*, clientes_telefonos(*), clientes_domicilios(*)').eq('id', clienteId).single(),
      supabase.from('expedientes_clientes').select('expedientes(*)').eq('cliente_id', clienteId),
      supabase.from('tramites_clientes').select('tramites(*)').eq('cliente_id', clienteId),
      supabase.from('ingresos').select('*').eq('cliente_id', clienteId).eq('eliminado', false).order('fecha', { ascending: false }),
      supabase.from('deudas').select('*').eq('cliente_id', clienteId).eq('eliminado', false).order('created_at', { ascending: false }),
      supabase.from('gastos').select('*').eq('cliente_id', clienteId).eq('eliminado', false).order('fecha', { ascending: false })
    ]);

    setCliente(cli);
    const { data: notasData } = await supabase.from('clientes_notas').select('*').eq('cliente_id', clienteId).eq('eliminado', false).order('created_at', { ascending: false });
    setNotas(notasData || []);
    const expsData = exps?.map(e => e.expedientes).filter(e => e && !e.eliminado) || [];
    setExpedientes(expsData);
    setTramites(trams?.map(t => t.tramites).filter(t => t && !t.eliminado) || []);
    setIngresos(ings || []);
    setDeudas(deuds || []);
    setGastos(gasts || []);

    const expIds = expsData.map(e => e.id);
    const tramIds = (trams?.map(t => t.tramites).filter(Boolean) || []).map(t => t.id);
    let todasTareas = [];
    if (expIds.length > 0) {
      const { data: tareasExp } = await supabase.from('tareas').select('*, expedientes(caratula)').in('expediente_id', expIds).eq('completada', false).eq('eliminado', false);
      todasTareas = [...todasTareas, ...(tareasExp || [])];
    }
    if (tramIds.length > 0) {
      const { data: tareasTram } = await supabase.from('tareas').select('*, tramites(nombre)').in('tramite_id', tramIds).eq('completada', false).eq('eliminado', false);
      todasTareas = [...todasTareas, ...(tareasTram || [])];
    }
    setTareasPendientes(todasTareas);

    // Agenda: directo por cliente_id + a través de expedientes (sin duplicados)
    let agendaItems = [];
    const { data: agDirecta } = await supabase.from('agenda').select('*, expedientes(caratula, numero)').eq('cliente_id', clienteId).eq('eliminado', false).order('fecha', { ascending: true });
    agendaItems = [...(agDirecta || [])];
    if (expIds.length > 0) {
      const { data: agExp } = await supabase.from('agenda').select('*, expedientes(caratula, numero)').in('expediente_id', expIds).eq('eliminado', false).order('fecha', { ascending: true });
      agendaItems = [...agendaItems, ...(agExp || [])];
    }
    const seen = new Set();
    agendaItems = agendaItems.filter(a => { if (seen.has(a.id)) return false; seen.add(a.id); return true; });
    agendaItems.sort((a, b) => a.fecha.localeCompare(b.fecha));
    setAgendaCliente(agendaItems);

    setLoading(false);
  }

  async function saveIngreso() {
    if (guardandoIngreso) return;
    if (!formIngreso.monto || parseMonto(formIngreso.monto) <= 0 || !formIngreso.fecha) { setToast('Monto y fecha son obligatorios'); setTimeout(() => setToast(null), 3000); return; }
    setGuardandoIngreso(true);
    await supabase.from('ingresos').insert({ ...formIngreso, monto: parseMonto(formIngreso.monto), cliente_id: clienteId });
    setGuardandoIngreso(false); setModalNuevoIngreso(false);
    setFormIngreso({ monto: '', fecha: '', concepto: '', medio_cobro: 'Efectivo' });
    setToast('Ingreso registrado ✓'); setTimeout(() => setToast(null), 3000); fetchTodo();
  }

  async function saveGasto() {
    if (guardandoGasto) return;
    if (!formGasto.monto || parseMonto(formGasto.monto) <= 0 || !formGasto.fecha) { setToast('Monto y fecha son obligatorios'); setTimeout(() => setToast(null), 3000); return; }
    setGuardandoGasto(true);
    await supabase.from('gastos').insert({ ...formGasto, monto: parseMonto(formGasto.monto), cliente_id: clienteId });
    setGuardandoGasto(false); setModalNuevoGasto(false);
    setFormGasto({ monto: '', fecha: '', concepto: '', categoria: '', medio_pago: 'Efectivo' });
    setToast('Gasto registrado ✓'); setTimeout(() => setToast(null), 3000); fetchTodo();
  }

  async function saveDeuda() {
    if (guardandoDeuda) return;
    if (!formDeuda.concepto || parseMonto(formDeuda.monto_total) <= 0) { setToast('Concepto y monto son obligatorios'); setTimeout(() => setToast(null), 3000); return; }
    setGuardandoDeuda(true);
    await supabase.from('deudas').insert({ ...formDeuda, monto_total: parseMonto(formDeuda.monto_total), monto_cobrado: 0, cliente_id: clienteId });
    setGuardandoDeuda(false); setModalNuevaDeuda(false);
    setFormDeuda({ concepto: '', monto_total: '', fecha_vencimiento: '' });
    setToast('Deuda registrada ✓'); setTimeout(() => setToast(null), 3000); fetchTodo();
  }

  async function saveNuevoExpediente() {
    if (guardandoExp) return;
    if (!formExp.caratula.trim()) { setToast('La carátula es obligatoria'); setTimeout(() => setToast(null), 3000); return; }
    setGuardandoExp(true);
    const { data: exp, error } = await supabase.from('expedientes').insert({
      caratula: formExp.caratula,
      numero: formExp.numero || null,
      fuero: (formExp.fuero && formExp.fuero !== '__otro__') ? formExp.fuero : null,
      posicion_procesal: formExp.posicion_procesal,
      estado_procesal: formExp.estado_procesal || null,
      juzgado: formExp.juzgado || null,
    }).select('id').single();
    if (error || !exp) { setToast('Error al crear expediente'); setGuardandoExp(false); return; }
    await supabase.from('expedientes_clientes').insert({ expediente_id: exp.id, cliente_id: clienteId });
    setGuardandoExp(false);
    setModalNuevoExp(false);
    setFormExp({ caratula: '', numero: '', fuero: '', posicion_procesal: 'actor', estado_procesal: '', juzgado: '' });
    setToast('Expediente creado ✓');
    setTimeout(() => setToast(null), 3000);
    fetchTodo();
    onUpdate?.();
  }

  async function saveNuevoTramite() {
    if (guardandoTram) return;
    if (!formTram.nombre.trim()) { setToast('El nombre es obligatorio'); setTimeout(() => setToast(null), 3000); return; }
    setGuardandoTram(true);
    const { data: tram, error } = await supabase.from('tramites').insert({
      nombre: formTram.nombre,
      tipo: formTram.tipo || null,
      fecha_inicio: formTram.fecha_inicio || null,
      fecha_vencimiento: formTram.fecha_vencimiento || null,
      estado: formTram.estado,
      parte_contraria: formTram.parte_contraria || null,
    }).select('id').single();
    if (error || !tram) { setToast('Error al crear trámite'); setGuardandoTram(false); return; }
    await supabase.from('tramites_clientes').insert({ tramite_id: tram.id, cliente_id: clienteId });
    setGuardandoTram(false);
    setModalNuevoTram(false);
    setFormTram({ nombre: '', tipo: '', fecha_inicio: '', fecha_vencimiento: '', estado: 'pendiente', parte_contraria: '' });
    setToast('Trámite creado ✓');
    setTimeout(() => setToast(null), 3000);
    fetchTodo();
    onUpdate?.();
  }

  async function addNota() {
    if (!nuevaNota.trim()) return;
    const { error } = await supabase.from('clientes_notas').insert({ cliente_id: clienteId, contenido: nuevaNota.trim() });
    if (error) { console.error('Error clientes_notas:', error); setToast('Error al guardar la nota'); setTimeout(() => setToast(null), 3000); return; }
    setNuevaNota('');
    const { data } = await supabase.from('clientes_notas').select('*').eq('cliente_id', clienteId).eq('eliminado', false).order('created_at', { ascending: false });
    setNotas(data || []);
    setToast('Nota guardada ✓');
    setTimeout(() => setToast(null), 3000);
  }

  async function deleteNota(id) {
    await softDelete('clientes_notas', id);
    setNotas(ns => ns.filter(n => n.id !== id));
  }

  const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR') : '—';
  const formatMoney = (n) => '$' + (n || 0).toLocaleString('es-AR');
  const today = new Date().toISOString().split('T')[0];
  const totalIngresos = ingresos.reduce((s, i) => s + (i.monto || 0), 0);
  const totalDeudas = deudas.reduce((s, d) => s + ((d.monto_total || 0) - (d.monto_cobrado || 0)), 0);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text4)' }}>
      Cargando...
    </div>
  );

  if (!cliente) return null;

  const estadoBadge = (e) => {
    const map = { activo: 'badge-green', inactivo: 'badge-red', potencial: 'badge-amber' };
    return <span className={`badge ${map[e] || 'badge-gray'}`}>{e}</span>;
  };

  const iniciales = `${cliente.nombre?.[0] || ''}${cliente.apellido?.[0] || ''}`.toUpperCase();

  if (verExpediente) return (
    <ExpedienteDetalle
      expedienteId={verExpediente}
      onClose={() => setVerExpediente(null)}
      onNavigateExpediente={(id) => setVerExpediente(id)}
      backLabel={`← ${cliente.nombre} ${cliente.apellido}`}
    />
  );

  if (verTramite) return (
    <TramiteDetalle
      tramiteId={verTramite}
      onClose={() => setVerTramite(null)}
      backLabel={`← ${cliente.nombre} ${cliente.apellido}`}
    />
  );

  return (
    <>
      <div className="drawer-header">
        <div className="drawer-header-top">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--blue-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 600, color: 'var(--blue)', flexShrink: 0 }}>
              {iniciales}
            </div>
            <div>
              <div className="drawer-title" style={{ marginBottom: 6 }}>{cliente.nombre} {cliente.apellido}</div>
              <div className="drawer-badges">
                {estadoBadge(cliente.estado)}
                {cliente.dni && <span className="badge badge-gray">DNI {cliente.dni}</span>}
                {cliente.fecha_primera_consulta && <span className="badge badge-gray">Desde {formatDate(cliente.fecha_primera_consulta)}</span>}
              </div>
            </div>
          </div>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>

        {/* Mini stats + botón editar */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'stretch' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, flex: 1 }}>
          {[
            { label: 'Expedientes', value: expedientes.length, color: 'var(--blue)' },
            { label: 'Trámites', value: tramites.length, color: '#7c3aed' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--surface2)', borderRadius: 8, padding: '10px 12px', border: '0.5px solid var(--border)' }}>
              <div style={{ fontSize: 10, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{s.label}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
        {onEdit && (
          <button
            onClick={() => onEdit(cliente)}
            className="btn btn-primary"
            style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, padding: '8px 14px', fontSize: 11 }}
          >
            <span style={{ display: 'block', textAlign: 'center', letterSpacing: 0.5 }}>EDITAR</span>
            <span style={{ display: 'block', textAlign: 'center', letterSpacing: 0.5 }}>FICHA</span>
          </button>
        )}
        </div>
      </div>{/* fin drawer-header */}

      <div className="drawer-tabs">
        {[
          { id: 'info', label: `Información y notas (${notas.length})` },
          { id: 'expedientes', label: `Expedientes (${expedientes.length})` },
          { id: 'tramites', label: `Trámites (${tramites.length})` },
          { id: 'tareas', label: `Tareas y Audiencias (${tareasPendientes.length + agendaCliente.length})` },
          { id: 'contable', label: `Contable` },
        ].map(tab => (
          <div key={tab.id} className={`drawer-tab ${tabActiva === tab.id ? 'active' : ''}`} onClick={() => setTabActiva(tab.id)}>
            {tab.label}
          </div>
        ))}
      </div>

      <div className="drawer-body">

        {/* INFORMACIÓN Y NOTAS */}
        {tabActiva === 'info' && (
          <div>
            {/* Datos del cliente */}
            <div style={{ background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
              <div className="info-grid">
                {[
                  { label: 'DNI', value: cliente.dni || '—' },
                  { label: 'CUIL / CUIT', value: cliente.cuil || '—' },
                  { label: 'Email', value: cliente.email || '—' },
                  { label: 'Fecha de nacimiento', value: formatDate(cliente.fecha_nacimiento) },
                  ...(cliente.clientes_telefonos?.length
                    ? cliente.clientes_telefonos.map(t => ({ label: `Tel. ${t.tipo}`, value: t.numero || '—' }))
                    : [{ label: 'Teléfono', value: '—' }]),
                  ...(cliente.clientes_domicilios?.length
                    ? cliente.clientes_domicilios.map(d => ({ label: `Dom. ${d.tipo}`, value: d.direccion || '—' }))
                    : [{ label: 'Domicilio', value: '—' }]),
                ].map(item => (
                  <div className="info-item" key={item.label}>
                    <label>{item.label}</label>
                    <span>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* NOTAS */}
            <div>
              <div className="section-label" style={{ marginBottom: 8 }}>Notas</div>
              <textarea
                className="form-textarea"
                value={nuevaNota}
                onChange={e => setNuevaNota(e.target.value)}
                placeholder="Escribí una nueva nota..."
                style={{ minHeight: 64, maxHeight: 100, resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6, marginBottom: 12 }}>
                <button className="btn btn-primary btn-sm" onClick={addNota} disabled={!nuevaNota.trim()}>
                  + Agregar nota
                </button>
              </div>
              {notas.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text4)' }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>📝</div>
                  <div style={{ fontSize: 13 }}>Sin notas</div>
                </div>
              ) : notas.map(n => (
                <div key={n.id} style={{ padding: '10px 14px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--text4)', fontFamily: 'DM Mono' }}>
                      {new Date(n.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {esAdmin && <button className="btn btn-danger btn-xs" onClick={() => deleteNota(n.id)}>🗑</button>}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{n.contenido}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EXPEDIENTES */}
        {tabActiva === 'expedientes' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button className="btn btn-primary btn-sm" onClick={() => setModalNuevoExp(true)}>+ Nuevo expediente</button>
            </div>
            {expedientes.length === 0 ? (
              <div className="empty-state"><div className="empty-state-icon">📁</div><div className="empty-state-title">Sin expedientes vinculados</div></div>
            ) : expedientes.map(e => (
              <div key={e.id} onClick={() => setVerExpediente(e.id)} style={{ padding: '13px 14px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8, marginBottom: 8, cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={ev => ev.currentTarget.style.borderColor = 'var(--blue)'}
                onMouseLeave={ev => ev.currentTarget.style.borderColor = 'var(--border)'}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text4)', fontFamily: 'DM Mono', marginBottom: 2 }}>{e.numero || 'S/N'}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{e.caratula}</div>
                    <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 3 }}>{e.fuero} · {e.estado_procesal || 'Sin estado'}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className={`badge ${e.posicion_procesal === 'actor' ? 'badge-green' : 'badge-amber'}`}>{e.posicion_procesal}</span>
                    <span style={{ color: 'var(--text4)', fontSize: 14 }}>›</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* TRAMITES */}
        {tabActiva === 'tramites' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button className="btn btn-primary btn-sm" onClick={() => setModalNuevoTram(true)}>+ Nuevo trámite</button>
            </div>
            {tramites.length === 0 ? (
              <div className="empty-state"><div className="empty-state-icon">📋</div><div className="empty-state-title">Sin trámites vinculados</div></div>
            ) : tramites.map(t => (
              <div key={t.id} onClick={() => setVerTramite(t.id)} style={{ padding: '13px 14px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8, marginBottom: 8, cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={ev => ev.currentTarget.style.borderColor = 'var(--blue)'}
                onMouseLeave={ev => ev.currentTarget.style.borderColor = 'var(--border)'}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{t.nombre}</div>
                    {t.tipo && <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>{t.tipo}</div>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className={`badge ${t.estado === 'completado' ? 'badge-green' : t.estado === 'en espera' ? 'badge-blue' : 'badge-amber'}`}>{t.estado}</span>
                    <span style={{ color: 'var(--text4)', fontSize: 14 }}>›</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* TAREAS Y AUDIENCIAS */}
        {tabActiva === 'tareas' && (() => {
          const colorMap = { 'Audiencia': '#ef4444', 'Vencimiento procesal': '#f59e0b', 'Consulta / Turno': '#10b981', 'Reunión': '#3b82f6', 'Vencimiento de deuda': '#f59e0b' };
          return (
          <div>
            {/* AUDIENCIAS */}
            <div className="section-label" style={{ marginBottom: 10 }}>Audiencias y eventos</div>
            {agendaCliente.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '16px 0 20px', color: 'var(--text4)' }}>
                <div style={{ fontSize: 26, marginBottom: 5 }}>📅</div>
                <div style={{ fontSize: 13 }}>Sin eventos agendados</div>
              </div>
            ) : agendaCliente.map(ev => {
              const color = colorMap[ev.tipo] || '#9ca3af';
              const pasado = ev.fecha < today;
              const realizado = ev.estado === 'realizado';
              return (
                <div key={ev.id} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderLeft: `3px solid ${realizado ? '#d1d5db' : color}`, borderRadius: 8, marginBottom: 7, opacity: realizado ? 0.6 : 1 }}>
                  {/* Fecha */}
                  <div style={{ flexShrink: 0, width: 44, background: realizado ? 'var(--surface)' : color + '18', borderRadius: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4px 2px' }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: realizado ? 'var(--text4)' : color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {new Date(ev.fecha + 'T12:00:00').toLocaleDateString('es-AR', { month: 'short' })}
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: realizado ? 'var(--text4)' : color, lineHeight: 1 }}>
                      {new Date(ev.fecha + 'T12:00:00').getDate()}
                    </div>
                    {ev.hora && <div style={{ fontSize: 9, color: realizado ? 'var(--text4)' : color, fontFamily: 'DM Mono', marginTop: 2 }}>{ev.hora.substring(0,5)}</div>}
                  </div>
                  {/* Contenido */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', textDecoration: realizado ? 'line-through' : 'none', marginBottom: 3 }}>{ev.titulo}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
                      <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: color + '18', color, fontWeight: 500 }}>{ev.tipo}</span>
                      {realizado && <span className="badge badge-green" style={{ fontSize: 9 }}>Realizado</span>}
                      {!realizado && pasado && <span className="badge badge-red" style={{ fontSize: 9 }}>Pasado</span>}
                    </div>
                    {ev.expedientes && <div style={{ fontSize: 10, color: 'var(--text4)', marginTop: 4 }}>📁 {ev.expedientes.caratula?.substring(0, 38)}</div>}
                  </div>
                </div>
              );
            })}

            {/* TAREAS */}
            <div className="section-label" style={{ marginBottom: 10, marginTop: 18 }}>Tareas pendientes</div>
            {tareasPendientes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text4)' }}>
                <div style={{ fontSize: 26, marginBottom: 5 }}>✅</div>
                <div style={{ fontSize: 13 }}>Sin tareas pendientes</div>
              </div>
            ) : tareasPendientes.map(t => {
              const vencida = t.fecha_vencimiento && t.fecha_vencimiento < today;
              const borderColor = t.prioridad === 'urgente' ? 'var(--red)' : 'var(--blue)';
              return (
                <div key={t.id} onClick={() => setTareaDetalleId(t.id)} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderLeft: `3px solid ${borderColor}`, borderRadius: 8, marginBottom: 7, alignItems: 'flex-start', cursor: 'pointer' }}
                  onMouseEnter={ev => ev.currentTarget.style.borderColor = borderColor}
                  onMouseLeave={ev => ev.currentTarget.style.borderColor = 'var(--border)'}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>{t.titulo}</div>
                    <div style={{ fontSize: 11, color: 'var(--text4)' }}>
                      {t.expedientes ? `📁 ${t.expedientes.caratula?.substring(0, 35)}` : t.tramites ? `📋 ${t.tramites.nombre?.substring(0, 35)}` : '—'}
                    </div>
                    {t.fecha_vencimiento && (
                      <div style={{ fontSize: 10, color: vencida ? 'var(--red)' : 'var(--text4)', marginTop: 3 }}>
                        ⏰ Vence {formatDate(t.fecha_vencimiento)}{vencida ? ' — VENCIDA' : ''}
                      </div>
                    )}
                  </div>
                  {t.prioridad === 'urgente' && <span className="badge badge-red" style={{ fontSize: 9, flexShrink: 0 }}>Urgente</span>}
                </div>
              );
            })}
          </div>
        );})()}

        {/* CONTABLE */}
        {tabActiva === 'contable' && (
          <div>
            {/* Botones de acción */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {[
                { label: '+ Ingreso', bg: '#dcfce7', color: '#166534', border: '#86efac', onClick: () => setModalNuevoIngreso(true) },
                { label: '+ Gasto',   bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', onClick: () => setModalNuevoGasto(true) },
                { label: '+ Deuda',   bg: '#fef3c7', color: '#92400e', border: '#fde68a', onClick: () => setModalNuevaDeuda(true) },
              ].map(b => (
                <button key={b.label} onClick={b.onClick} style={{ background: b.bg, color: b.color, border: `0.5px solid ${b.border}`, borderRadius: 7, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {b.label}
                </button>
              ))}
            </div>

            {/* Ingresos */}
            <div className="section-label" style={{ marginBottom: 8 }}>Ingresos</div>
            {ingresos.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text4)', marginBottom: 16, padding: '8px 0' }}>Sin ingresos registrados</div>
            ) : ingresos.map(i => (
              <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderLeft: '3px solid var(--green)', borderRadius: 8, marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text2)' }}>{i.concepto || 'Sin concepto'}</div>
                  <div style={{ fontSize: 11, color: 'var(--text4)' }}>{formatDate(i.fecha)} · {i.medio_cobro}</div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--green)' }}>{formatMoney(i.monto)}</div>
              </div>
            ))}

            {/* Gastos */}
            <div className="section-label" style={{ marginTop: 16, marginBottom: 8 }}>Gastos</div>
            {gastos.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text4)', marginBottom: 16, padding: '8px 0' }}>Sin gastos registrados</div>
            ) : gastos.map(g => (
              <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderLeft: '3px solid var(--red)', borderRadius: 8, marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--text2)' }}>{g.concepto || 'Sin concepto'}</div>
                  <div style={{ fontSize: 11, color: 'var(--text4)' }}>{formatDate(g.fecha)}{g.categoria ? ` · ${g.categoria}` : ''} · {g.medio_pago}</div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--red)' }}>{formatMoney(g.monto)}</div>
              </div>
            ))}

            {/* Deudas */}
            <div className="section-label" style={{ marginTop: 16, marginBottom: 8 }}>Deudas a cobrar</div>
            {deudas.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text4)', padding: '8px 0' }}>Sin deudas registradas</div>
            ) : deudas.map(d => {
              const saldo = (d.monto_total || 0) - (d.monto_cobrado || 0);
              return (
                <div key={d.id} style={{ padding: '13px 14px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderLeft: '3px solid var(--amber)', borderRadius: 8, marginBottom: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{d.concepto}</div>
                      <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>
                        Total {formatMoney(d.monto_total)} · Cobrado {formatMoney(d.monto_cobrado)}
                        {d.fecha_vencimiento && ` · Vence ${formatDate(d.fecha_vencimiento)}`}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: saldo > 0 ? 'var(--amber)' : 'var(--green)' }}>{formatMoney(saldo)}</div>
                      <div style={{ fontSize: 10, color: 'var(--text4)' }}>saldo</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>


      {/* MODAL NUEVO EXPEDIENTE */}
      {modalNuevoExp && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalNuevoExp(false)}>
          <div className="modal" style={{ width: 560 }}>
            <div className="modal-header">
              <div className="modal-title">Nuevo expediente</div>
              <button className="modal-close" onClick={() => setModalNuevoExp(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: 'var(--blue)', background: 'var(--blue-light)', border: '0.5px solid #bfdbfe', borderRadius: 7, padding: '8px 12px', marginBottom: 14 }}>
                Se vinculará automáticamente a <strong>{cliente?.nombre} {cliente?.apellido}</strong>
              </div>
              <div className="form-grid">
                <div className="form-group form-full" style={{ gap: 3 }}>
                  <label className="form-label">Carátula *</label>
                  <input className="form-input" value={formExp.caratula} onChange={e => setFormExp(f => ({ ...f, caratula: e.target.value }))} placeholder="Ej: GARCÍA c/ EMPRESA S.A. s/ Despido" />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Número de expediente</label>
                  <input className="form-input" value={formExp.numero} onChange={e => setFormExp(f => ({ ...f, numero: e.target.value }))} placeholder="Ej: 12345/2024" />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Posición procesal</label>
                  <select className="form-select" value={formExp.posicion_procesal} onChange={e => setFormExp(f => ({ ...f, posicion_procesal: e.target.value }))}>
                    <option value="actor">Actor</option>
                    <option value="demandado">Demandado</option>
                  </select>
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Fuero</label>
                  {!(formExp.fuero && !FUEROS.includes(formExp.fuero)) ? (
                    <select className="form-select" value={formExp.fuero} onChange={e => setFormExp(f => ({ ...f, fuero: e.target.value }))}>
                      <option value="">Seleccioná</option>
                      {FUEROS.map(f => <option key={f}>{f}</option>)}
                      <option value="__otro__">Otro...</option>
                    </select>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="form-input" autoFocus value={formExp.fuero === '__otro__' ? '' : formExp.fuero}
                        onChange={e => setFormExp(f => ({ ...f, fuero: e.target.value || '__otro__' }))} placeholder="Especificá el fuero" />
                      <button className="btn btn-ghost btn-sm" onClick={() => setFormExp(f => ({ ...f, fuero: '' }))}>✕</button>
                    </div>
                  )}
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Estado procesal</label>
                  <input className="form-input" value={formExp.estado_procesal} onChange={e => setFormExp(f => ({ ...f, estado_procesal: e.target.value }))} placeholder="Ej: Inicio, Prueba..." />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Juzgado</label>
                  <input className="form-input" value={formExp.juzgado} onChange={e => setFormExp(f => ({ ...f, juzgado: e.target.value }))} placeholder="Ej: Juzgado N° 3" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalNuevoExp(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveNuevoExpediente} disabled={guardandoExp}>
                {guardandoExp ? 'Guardando...' : 'Crear expediente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NUEVO INGRESO */}
      {modalNuevoIngreso && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalNuevoIngreso(false)}>
          <div className="modal" style={{ width: 460 }}>
            <div className="modal-header"><div className="modal-title">Registrar ingreso</div><button className="modal-close" onClick={() => setModalNuevoIngreso(false)}>✕</button></div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: 'var(--blue)', background: 'var(--blue-light)', border: '0.5px solid #bfdbfe', borderRadius: 7, padding: '8px 12px', marginBottom: 14 }}>
                Se vinculará a <strong>{cliente?.nombre} {cliente?.apellido}</strong>
              </div>
              <div className="form-grid">
                <div className="form-group" style={{ gap: 3 }}><label className="form-label">Monto *</label><MontoInput value={formIngreso.monto} onChange={v => setFormIngreso(f => ({ ...f, monto: v }))} /></div>
                <div className="form-group" style={{ gap: 3 }}><label className="form-label">Fecha *</label><input className="form-input" type="date" value={formIngreso.fecha} onChange={e => setFormIngreso(f => ({ ...f, fecha: e.target.value }))} /></div>
                <div className="form-group form-full" style={{ gap: 3 }}><label className="form-label">Concepto</label><input className="form-input" value={formIngreso.concepto} onChange={e => setFormIngreso(f => ({ ...f, concepto: e.target.value }))} placeholder="Ej: Honorarios, Cuota..." /></div>
                <div className="form-group form-full" style={{ gap: 3 }}><label className="form-label">Medio de cobro</label>
                  <select className="form-select" value={formIngreso.medio_cobro} onChange={e => setFormIngreso(f => ({ ...f, medio_cobro: e.target.value }))}>
                    <option>Efectivo</option><option>Transferencia</option><option>Cheque</option><option>Otro</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-ghost" onClick={() => setModalNuevoIngreso(false)}>Cancelar</button><button className="btn btn-primary" onClick={saveIngreso} disabled={guardandoIngreso}>{guardandoIngreso ? 'Guardando...' : 'Registrar ingreso'}</button></div>
          </div>
        </div>
      )}

      {/* MODAL NUEVO GASTO */}
      {modalNuevoGasto && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalNuevoGasto(false)}>
          <div className="modal" style={{ width: 460 }}>
            <div className="modal-header"><div className="modal-title">Registrar gasto</div><button className="modal-close" onClick={() => setModalNuevoGasto(false)}>✕</button></div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: 'var(--blue)', background: 'var(--blue-light)', border: '0.5px solid #bfdbfe', borderRadius: 7, padding: '8px 12px', marginBottom: 14 }}>
                Se vinculará a <strong>{cliente?.nombre} {cliente?.apellido}</strong>
              </div>
              <div className="form-grid">
                <div className="form-group" style={{ gap: 3 }}><label className="form-label">Monto *</label><MontoInput value={formGasto.monto} onChange={v => setFormGasto(f => ({ ...f, monto: v }))} /></div>
                <div className="form-group" style={{ gap: 3 }}><label className="form-label">Fecha *</label><input className="form-input" type="date" value={formGasto.fecha} onChange={e => setFormGasto(f => ({ ...f, fecha: e.target.value }))} /></div>
                <div className="form-group form-full" style={{ gap: 3 }}><label className="form-label">Concepto</label><input className="form-input" value={formGasto.concepto} onChange={e => setFormGasto(f => ({ ...f, concepto: e.target.value }))} placeholder="Descripción del gasto" /></div>
                <div className="form-group" style={{ gap: 3 }}><label className="form-label">Categoría</label><input className="form-input" value={formGasto.categoria} onChange={e => setFormGasto(f => ({ ...f, categoria: e.target.value }))} placeholder="Ej: Sellado, Peritos..." /></div>
                <div className="form-group" style={{ gap: 3 }}><label className="form-label">Medio de pago</label>
                  <select className="form-select" value={formGasto.medio_pago} onChange={e => setFormGasto(f => ({ ...f, medio_pago: e.target.value }))}>
                    <option>Efectivo</option><option>Transferencia</option><option>Cheque</option><option>Otro</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-ghost" onClick={() => setModalNuevoGasto(false)}>Cancelar</button><button className="btn btn-primary" onClick={saveGasto} disabled={guardandoGasto}>{guardandoGasto ? 'Guardando...' : 'Registrar gasto'}</button></div>
          </div>
        </div>
      )}

      {/* MODAL NUEVA DEUDA */}
      {modalNuevaDeuda && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalNuevaDeuda(false)}>
          <div className="modal" style={{ width: 460 }}>
            <div className="modal-header"><div className="modal-title">Registrar deuda a cobrar</div><button className="modal-close" onClick={() => setModalNuevaDeuda(false)}>✕</button></div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: 'var(--blue)', background: 'var(--blue-light)', border: '0.5px solid #bfdbfe', borderRadius: 7, padding: '8px 12px', marginBottom: 14 }}>
                Se vinculará a <strong>{cliente?.nombre} {cliente?.apellido}</strong>
              </div>
              <div className="form-grid">
                <div className="form-group form-full" style={{ gap: 3 }}><label className="form-label">Concepto *</label><input className="form-input" value={formDeuda.concepto} onChange={e => setFormDeuda(f => ({ ...f, concepto: e.target.value }))} placeholder="Ej: Honorarios pendientes..." /></div>
                <div className="form-group" style={{ gap: 3 }}><label className="form-label">Monto total *</label><MontoInput value={formDeuda.monto_total} onChange={v => setFormDeuda(f => ({ ...f, monto_total: v }))} /></div>
                <div className="form-group" style={{ gap: 3 }}><label className="form-label">Fecha de vencimiento</label><input className="form-input" type="date" value={formDeuda.fecha_vencimiento} onChange={e => setFormDeuda(f => ({ ...f, fecha_vencimiento: e.target.value }))} /></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-ghost" onClick={() => setModalNuevaDeuda(false)}>Cancelar</button><button className="btn btn-primary" onClick={saveDeuda} disabled={guardandoDeuda}>{guardandoDeuda ? 'Guardando...' : 'Registrar deuda'}</button></div>
          </div>
        </div>
      )}

      {/* MODAL NUEVO TRÁMITE */}
      {modalNuevoTram && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalNuevoTram(false)}>
          <div className="modal" style={{ width: 540 }}>
            <div className="modal-header">
              <div className="modal-title">Nuevo trámite</div>
              <button className="modal-close" onClick={() => setModalNuevoTram(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: 'var(--blue)', background: 'var(--blue-light)', border: '0.5px solid #bfdbfe', borderRadius: 7, padding: '8px 12px', marginBottom: 14 }}>
                Se vinculará automáticamente a <strong>{cliente?.nombre} {cliente?.apellido}</strong>
              </div>
              <div className="form-grid">
                <div className="form-group form-full" style={{ gap: 3 }}>
                  <label className="form-label">Nombre *</label>
                  <input className="form-input" value={formTram.nombre} onChange={e => setFormTram(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Carta documento, Pedido de informes" />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Tipo</label>
                  <input className="form-input" value={formTram.tipo} onChange={e => setFormTram(f => ({ ...f, tipo: e.target.value }))} placeholder="Ej: Epistolar, Administrativo" />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Estado</label>
                  <select className="form-select" value={formTram.estado} onChange={e => setFormTram(f => ({ ...f, estado: e.target.value }))}>
                    <option value="pendiente">Pendiente</option>
                    <option value="en curso">En curso</option>
                    <option value="en espera">En espera</option>
                    <option value="completado">Completado</option>
                    <option value="archivado">Archivado</option>
                  </select>
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Parte contraria</label>
                  <input className="form-input" value={formTram.parte_contraria} onChange={e => setFormTram(f => ({ ...f, parte_contraria: e.target.value }))} placeholder="Nombre de la parte contraria" />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Fecha de inicio</label>
                  <input className="form-input" type="date" value={formTram.fecha_inicio} onChange={e => setFormTram(f => ({ ...f, fecha_inicio: e.target.value }))} />
                </div>
                <div className="form-group" style={{ gap: 3 }}>
                  <label className="form-label">Fecha de vencimiento</label>
                  <input className="form-input" type="date" value={formTram.fecha_vencimiento} onChange={e => setFormTram(f => ({ ...f, fecha_vencimiento: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalNuevoTram(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveNuevoTramite} disabled={guardandoTram}>
                {guardandoTram ? 'Guardando...' : 'Crear trámite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">✓ {toast}</div>}

      {tareaDetalleId && (
        <TareaDetalle
          tareaId={tareaDetalleId}
          onClose={() => setTareaDetalleId(null)}
          onCompletar={() => { fetchTodo(); setTareaDetalleId(null); }}
          onEdit={(tarea) => { setTareaDetalleId(null); setTareaEdit(tarea); }}
        />
      )}

      {/* Modal edición tarea */}
      <TareaModal
        open={!!tareaEdit}
        editing={tareaEdit}
        onClose={() => setTareaEdit(null)}
        onSaved={() => { setTareaEdit(null); fetchTodo(); }}
      />
    </>
  );
}

// Formatea número al estilo argentino: 1.234,56
function MontoInput({ value, onChange, placeholder = '0,00' }) {
  function handleChange(e) {
    let raw = e.target.value.replace(/[^\d,]/g, ''); // solo dígitos y coma
    const parts = raw.split(',');
    if (parts.length > 2) raw = parts[0] + ',' + parts.slice(1).join(''); // una sola coma
    const intPart = (parts[0] || '').replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // puntos de miles
    const formatted = parts.length > 1 ? intPart + ',' + parts[1].substring(0, 2) : intPart;
    onChange(formatted);
  }
  return (
    <input
      className="form-input"
      type="text"
      inputMode="decimal"
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
    />
  );
}

export function parseMonto(str) {
  if (!str) return 0;
  return parseFloat(str.replace(/\./g, '').replace(',', '.')) || 0;
}